import { useState, useRef } from "react";
import { ChevronDown, Video, Layers, Globe, ArrowRight, Play, Network } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LearnMegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };
  
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };
  
  const handleNavigate = (href: string) => {
    navigate(href);
    setIsOpen(false);
  };
  
  return (
    <div className="relative hidden md:block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-foreground/80 transition-colors rounded-full hover:bg-muted/50 outline-none">
        Learn
        <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Mega Menu */}
      <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 transition-all duration-300 ${isOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-3 pointer-events-none"}`}>
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-[420px] p-5 backdrop-blur-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/50">
            <h3 className="text-sm font-semibold text-foreground">Learn</h3>
            <span className="text-xs text-muted-foreground">Explore resources</span>
          </div>

          <div className="space-y-2">
            {/* Videos Card */}
            <button 
              onClick={() => handleNavigate("/videos")} 
              className="group w-full p-4 rounded-xl border border-border/50 bg-muted/30 hover:bg-muted/50 transition-all duration-300 hover:border-muted-foreground/30 text-left" 
              style={{ animation: isOpen ? 'dropdownItemIn 0.4s ease-out 0ms both' : 'none' }}
            >
              <div className="flex items-start gap-4">
                {/* Icon Container */}
                <div className="relative p-3 rounded-xl bg-gradient-to-br from-foreground/10 to-foreground/5 border border-border/50 group-hover:border-foreground/20 transition-all duration-300">
                  <Video className="h-5 w-5 text-foreground" />
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-foreground/90 flex items-center justify-center">
                    <Play className="h-2.5 w-2.5 text-background ml-0.5" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-foreground group-hover:text-foreground/80 transition-colors">
                      Videos
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                    Watch tutorials and deep dives into DePIN technology.
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-foreground/10 text-foreground font-medium">
                      1+ Video
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      New weekly
                    </span>
                  </div>
                </div>
              </div>
            </button>

            {/* Ecosystem Card */}
            <button 
              onClick={() => handleNavigate("/ecosystem")} 
              className="group w-full p-4 rounded-xl border border-border/50 bg-muted/30 hover:bg-muted/50 transition-all duration-300 hover:border-muted-foreground/30 text-left" 
              style={{ animation: isOpen ? 'dropdownItemIn 0.4s ease-out 100ms both' : 'none' }}
            >
              <div className="flex items-start gap-4">
                {/* Icon Container */}
                <div className="relative p-3 rounded-xl bg-gradient-to-br from-foreground/10 to-foreground/5 border border-border/50 group-hover:border-foreground/20 transition-all duration-300">
                  <Network className="h-5 w-5 text-foreground" />
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-foreground/90 flex items-center justify-center">
                    <Globe className="h-2.5 w-2.5 text-background" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-foreground group-hover:text-foreground/80 transition-colors">
                      Ecosystem
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                    Explore the DePIN Land network and community roles.
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-foreground/10 text-foreground font-medium">
                      6 Roles
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      NFT
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnMegaMenu;
